import { bookingSeasonClosedTerminal } from "./bookingSeasonGate";
import { isCalThrottled } from "@/lib/calThrottled";
import type { TerminalState } from "./publicBookingFlowSlice";
import { PROGRAM_REFUSED_CODES, PROGRAM_REFUSED_TERMINAL } from "./publicBookingFlowTypes";

/**
 * What the booking step does with a failed POST api/ams/bookings.
 *
 * - `seasonClosed`: the booking_season_closed terminal (409 code bookingSeasonClosed).
 * - `slotTaken`: the slot_taken terminal (409 code slotUnavailable).
 * - `programRefused`: the program_refused terminal (409 code programAlreadyReceived or
 *   programDisabled), carrying the server's message.
 * - `householdAlreadyBooked`: the hh_existing_appointment terminal (any other 409).
 * - `heldTimeGone`: the held_time_gone terminal, which offers "Find another time" — see below.
 * - `throttled`: the scheduling service is busy (503 calThrottled, AB#514). Nothing was booked; the
 *   step counts down the server's wait and tries the same hold again, once by itself.
 * - `retryable`: a network failure or 5xx, retried once on the same hold; `message` is shown if the
 *   retry fails too.
 * - `error`: the "Unable to Schedule" card with Try Again, and the message to show on it.
 */
export type CreateBookingFailure =
  | { kind: "seasonClosed"; terminal: TerminalState }
  | { kind: "slotTaken" }
  | { kind: "programRefused"; terminal: TerminalState }
  | { kind: "householdAlreadyBooked" }
  | { kind: "heldTimeGone" }
  | { kind: "throttled"; error: unknown }
  | { kind: "retryable"; message: string }
  | { kind: "error"; message: string };

export function classifyCreateBookingFailure(err: unknown): CreateBookingFailure {
  // Booking closed while this applicant was filling in the form. Checked before the duplicate
  // refusal below, which takes any 409 as its own: this one is told apart by its code.
  const seasonClosed = bookingSeasonClosedTerminal(err);
  if (seasonClosed) {
    return { kind: "seasonClosed", terminal: seasonClosed };
  }
  if (isSlotTaken(err)) {
    return { kind: "slotTaken" };
  }
  // M9: no funding program remains. Also before the duplicate refusal, which would otherwise take it.
  if (isProgramRefused(err)) {
    return { kind: "programRefused", terminal: programRefusedTerminal(err) };
  }
  if (isHouseholdAlreadyBooked(err)) {
    return { kind: "householdAlreadyBooked" };
  }
  if (isHeldTimeGone(err)) {
    return { kind: "heldTimeGone" };
  }
  // Before the retryable 5xx below, which would retry it at once — into the same throttle.
  if (isCalThrottled(err)) {
    return { kind: "throttled", error: err };
  }
  if (isRetryableFailure(err)) {
    return { kind: "retryable", message: getErrorMessage(err) };
  }
  return { kind: "error", message: getErrorMessage(err) };
}

/**
 * A 422 that means the time this booking was held against is gone, so resubmitting the same hold
 * can only fail again. AmsBookingsController.CreatePublicBooking answers 422 with a body of exactly
 * `{ error }` for three outcomes:
 *
 * - ReservationNotFound: "Reservation not found or expired for this session."
 * - AlreadyBooked: "That appointment time is no longer available. Please reserve another slot."
 * - anything else (SiteNotProvisioned): "The appointment configuration changed. Please reserve another slot."
 *
 * All three ask for another hold, which is what "Find another time" gets.
 *
 * Matched on the shape — a 422 whose body carries an `error` string and nothing else — rather than
 * on the wording, which is copy and may change. That excludes, on purpose:
 *
 * - MissingInformation, the fourth 422, whose body adds `missingInformation`: a new hold would be
 *   refused for the same reason, so it keeps the existing error card.
 * - Any 422 that carries a `code` (or any other field). Machine-readable refusals added later — the
 *   eligibility refusals planned for M9, say — are not "your time is gone", and finding another time
 *   would only be refused again. They fall through to the existing error card until they are given a
 *   screen of their own. A code that does mean "held time gone" must be added here explicitly.
 */
export function isHeldTimeGone(err: unknown): boolean {
  if (statusOf(err) !== 422) {
    return false;
  }
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }
  const keys = Object.keys(data);
  return keys.length === 1 && keys[0] === "error" && typeof (data as { error?: unknown }).error === "string";
}

// Only retry transient failures (network errors, 5xx) — a 4xx like 422 (missing attendee info) will
// fail identically on retry, so retrying it just wastes a round trip and risks masking the real error.
//
// Retrying a create at all is only safe because the server treats the slot reservation as the
// idempotency key: a repeat submission of the same reservation is answered with the booking it
// already made (see PublicBookingOperations.CreateBookingAsync) rather than making a second one.
// Widening this to a failure carrying no reservation would be double-booking people.
export function isRetryableFailure(err: unknown): boolean {
  if (!err || typeof err !== "object" || !("status" in err)) {
    return false;
  }
  const status = (err as { status: unknown }).status;
  if (typeof status === "number") {
    return status >= 500;
  }
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
}

// Apart from the bookingSeasonClosed refusal, which is checked for first, 409 is MSC's
// duplicate-appointment refusal: the Social Security number submitted exactly matches
// one on a valid upcoming appointment, of any event type, so no second appointment is made. See
// PublicBookingCreateStatus.HouseholdAlreadyBooked.
//
// The flow normally catches this at step 2 and never reaches here. It reaches here when the household
// booked in another tab in between, or when the number was not checked at step 2 for any reason. The
// server is the block; this is the page catching up with it, and it must land on the same terminal
// the step-2 check does rather than in the generic "Unable to Schedule" card — that one offers a
// Try Again button, and retrying a refusal just refuses again.
function isHouseholdAlreadyBooked(err: unknown): boolean {
  return statusOf(err) === 409 && !isSlotTaken(err) && !isProgramRefused(err);
}

// M9's refusals: the household has already received the program this year, or the program is switched
// off (PublicBookingCreateStatus.ProgramAlreadyReceived / ProgramDisabled). Coded so they are not
// mistaken for the household refusal above, and kept off "Find another time": another time is refused
// for the same reason.
function isProgramRefused(err: unknown): boolean {
  const code = (err as { data?: { code?: unknown } } | null)?.data?.code;
  return statusOf(err) === 409 && typeof code === "string" && PROGRAM_REFUSED_CODES.includes(code);
}

function programRefusedTerminal(err: unknown): TerminalState {
  const error = (err as { data?: { error?: unknown } }).data?.error;
  return { type: PROGRAM_REFUSED_TERMINAL, detail: { message: typeof error === "string" ? error : null } };
}

// The other 409: the host the scheduling system picked was booked by somebody else a moment before
// this booking could be recorded, so the server undid it (PublicBookingCreateStatus.SlotTaken). Coded
// so it is not mistaken for the household refusal above — this person has no appointment and can book
// another time, and the hold they had is gone, so Try Again would only report it expired.
function isSlotTaken(err: unknown): boolean {
  return statusOf(err) === 409 && (err as { data?: { code?: unknown } }).data?.code === "slotUnavailable";
}

function statusOf(err: unknown): unknown {
  return err && typeof err === "object" && "status" in err ? (err as { status: unknown }).status : undefined;
}

export function getErrorMessage(err: unknown): string {
  if (err && typeof err === "object") {
    const data = "data" in err ? (err as { data?: unknown }).data : undefined;
    if (data && typeof data === "object") {
      const dataObj = data as { error?: unknown; message?: unknown };
      if (typeof dataObj.error === "string") {
        return dataObj.error;
      }
      if (typeof dataObj.message === "string") {
        return dataObj.message;
      }
    }
    if ("message" in err && typeof (err as { message?: unknown }).message === "string") {
      return (err as { message: string }).message;
    }
  }
  return "Appointment could not be created.";
}
